import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { getAwardsBgColor } from '$utilities/data';
import { Image } from '$partials/media';
import { linearScale } from '$utilities/scale';
import { useAnchor } from '$contexts/anchor';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const ModuleAwardsSlide = ({
    article,
    previousTypename
}) => {
    // misc
    const { setCurrent } = useAnchor();

    // references
    const imageRef = useRef();
    const moduleRef = useRef();
    const textRef = useRef();
    const winnerRef = useRef();

    // state
    const [ screenSize, setScreenSize ] = useState('small');

    // methods
    const handleResize = () => {
        if (window.innerWidth < 1024) {
            if (screenSize !== 'small') {
                setScreenSize('small');
            }

            if (imageRef.current) {
                imageRef.current.style.transform = '';
            }

            if (textRef.current) {
                textRef.current.style.opacity = 1;
            }

            if (winnerRef.current) {
                winnerRef.current.style.backgroundColor = 'var(--bg-color)';
                winnerRef.current.style.backgroundImage = '';
            }
        } else {
            if (screenSize !== 'large') {
                setScreenSize('large');
            }
        }
    };

    const handleScroll = () => {
        if (window.innerWidth > 1023) {
            const moduleBottom = (moduleRef.current.offsetTop + moduleRef.current.offsetHeight);
            const moduleTop = (moduleRef.current.offsetTop - moduleRef.current.offsetHeight); // position = when module top enters bottom of viewport (module = 100vh)

            // range 1 start = half of module height above module top, range 1 end = when quarter module height above module top reaches top of viewport
            const backgroundImageValue = linearScale(window.scrollY, moduleRef.current.offsetTop - (moduleRef.current.offsetHeight / 2), moduleRef.current.offsetTop - (moduleRef.current.offsetHeight / 4), 0, 100);

            // –
            const transformValue = linearScale(window.scrollY, moduleTop, moduleBottom, 0, 80);

            // range 1 end = when module top reaches top of viewport
            const opacityValue = linearScale(window.scrollY, moduleTop, moduleRef.current.offsetTop, 0.1, 1);

            if (imageRef.current) {
                imageRef.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
            }

            if (textRef.current) {
                textRef.current.style.opacity = opacityValue;
            }

            if (winnerRef.current) {
                winnerRef.current.style.backgroundColor = '';
                winnerRef.current.style.backgroundImage = `linear-gradient(90deg, var(--bg-color) ${backgroundImageValue}%, rgba(0, 0, 0, 0) ${backgroundImageValue}%)`;
            }
        }
    };

    // hooks
    useEffect(() => {
        const rafResize = () => window.requestAnimationFrame(handleResize);
        const rafScroll = () => window.requestAnimationFrame(handleScroll);

        window.addEventListener('resize', rafResize, { passive: true });
        window.addEventListener('scroll', rafScroll, { passive: true });

        rafResize();
        rafScroll();

        return () => {
            window.removeEventListener('resize', rafResize);
            window.removeEventListener('scroll', rafScroll);
        };
    }, [
        imageRef.current,
        moduleRef.current,
        screenSize,
        textRef.current
    ]);

    // is element in viewport
    useEffect(() => {
        if (moduleRef.current && typeof IntersectionObserver === 'function') {
            const handler = entries => {
                if (entries[0]?.isIntersecting) {
                    setCurrent(entries[0]?.target?.id);
                }
            };

            const observer = new IntersectionObserver(handler, { rootMargin: '-50% 0px -50% 0px' });
            observer.observe(moduleRef.current);

            return () => {
                observer.disconnect();
            };
        }

        return () => {};
    }, [ moduleRef.current ]);

    // misc
    const bg = getAwardsBgColor(article?.highlightColor);
    const image = article?.listingFields?.listingImage || article?.heroImage?.image;

    // render
    return (
        <section
            className={`layout-container h-[100vh] flex items-center${previousTypename === 'BvaMagazineAwardsModuleSlide' ? '' : ' overflow-hidden'}`}
            id={article?.slug}
            ref={moduleRef}>
            <div className='text-white px-10 md:px-20'>
                <div className='flex flex-col md:flex-row gap-20 md:items-center'>
                    <div
                        className='flex flex-col md:w-1/2'
                        ref={textRef}>
                        {/* Header */}
                        {article?.title && (
                            <h2 className='type-style-awards-heading-uppercase'>
                                {article.title}
                            </h2>
                        )}

                        {/* Winner */}
                        {article?.winner && (
                            <h3
                                className='text-black w-fit type-style-awards-tiny-uppercase md:type-style-awards-small-uppercase pl-10 py-7 md:py-5 mt-5 md:mt-10 pr-60'
                                ref={winnerRef}
                                style={{ '--bg-color': bg }}>
                                <span>Winnner: {article.winner}</span>
                            </h3>
                        )}

                        {/* Text */}
                        {article?.listingFields?.listingText && (
                            <p className='md:w-4/6 mt-20 md:mt-40 type-style-awards-body'>
                                {article.listingFields.listingText}
                            </p>
                        )}

                        {/* Link */}
                        {article?.slug && (
                            <a
                                className='type-style-awards-small underline mt-30 hidden md:block'
                                href={`/year-in-sport/articles/${article.slug}`}
                                rel='noreferrer'
                                target='_blank'>
                                Learn More
                            </a>
                        )}
                    </div>

                    {image && (
                        <figure
                            className='aspect-[4/3] h-full w-full md:w-1/2'
                            ref={imageRef}>
                            <div className='h-full w-full md:translate-y-1/4'>
                                <Image
                                    className='h-full w-full object-cover'
                                    containerClassName='h-full w-full pointer-events-none'
                                    containerStyle={{
                                        height: '100%',
                                        paddingBottom: 0
                                    }}
                                    loading={image.src}
                                    loadWidth={565}
                                    {...image} />

                                <figcaption className='hidden md:flex justify-between type-style-awards-tiny-uppercase pb-5 pt-5'>
                                    <span className='inline-flex justify-between'>
                                        <span role='presentation'>
                                            <img
                                                className='w-15 mr-5 pointer-events-none'
                                                height={article?.heroImageCaptionIcon?.height}
                                                loading='lazy'
                                                role='presentation'
                                                src={article?.heroImageCaptionIcon?.src}
                                                width={article?.heroImageCaptionIcon?.width} />
                                        </span>
                                        <span>{article?.heroImageCaptionCol1}</span>
                                    </span>

                                    <span>{article?.heroImageCaptionCol2}</span>
                                    <span>{article?.heroImageCaptionCol3}</span>
                                </figcaption>
                            </div>
                        </figure>
                    )}

                    {article?.slug && (
                        <a
                            className='type-style-awards-small underline md:hidden'
                            href={`/year-in-sport/articles/${article.slug}`}>
                            Learn More
                        </a>
                    )}
                </div>
            </div>
        </section>
    );
};

/* =========================================================================  */
/* Typing and export(s) */
/* =========================================================================  */

const propTypes = {
    article: PropTypes.object.isRequired,
    previousTypename: PropTypes.string
};

ModuleAwardsSlide.propTypes = propTypes;

export default ModuleAwardsSlide;
